import React, { FC, useEffect, useState } from 'react';
import { Col, Layout, Row } from 'antd';
import { useMediaPredicate } from 'react-media-hook';
import moment from 'moment';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { PageHeaderMob } from 'common/components/Page/PageHeaderMob';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { PeriodFilterCard } from 'common/components/PeriodFilterCard';
import { PeriodFilterToolbar } from 'common/components/PeriodFilterToolbar';
import { PageContent } from 'common/components/Page/PageContent';
import { ESidebarMenuKey, ESidebarMenuLabel, sidebarMenuIcons } from 'common/models/commonModel';
import { getFilterWeek } from 'common/helpers/date.helper';
import { usePeriodFilter } from 'common/hooks/usePeriodFilter';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getImproveQscStoreCollection } from 'app/store/reducers/improveQsc.reducer';
import { ImproveQscSummaryBlock } from 'entities/ImproveQsc/components/ImproveQscSummaryBlock';
import { ImproveQscFindingsBlock } from 'entities/ImproveQsc/components/ImproveQscFindingsBlock';
import { ImproveQscStoreCards } from 'entities/ImproveQsc/components/ImproveQscStoreCards';

export const ImproveQscPage: FC = () => {
  const dispatch = useAppDispatch();

  const { improveQscStoreCollection } = useAppSelector((state) => state.improveQsc);

  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  const [storeCode, setStoreCode] = useState<string | number | undefined>(undefined);

  const periodFilter = usePeriodFilter();

  useEffect(() => {
    dispatch(getImproveQscStoreCollection());
  }, []);

  const changeStore = (value?: string | number) => {
    setStoreCode(value);
  };

  const resolvedWeek = periodFilter.week ?? moment().isoWeek();
  const resolvedYear = periodFilter.year ?? moment().isoWeekYear();
  const weekStart = moment().isoWeekYear(resolvedYear).isoWeek(resolvedWeek).startOf('isoWeek');
  const weekEnd = weekStart.clone().endOf('isoWeek');
  const coverage = `${weekStart.format('MMM D, YYYY')} to ${weekEnd.format('MMM D, YYYY')} (12:00 am — 11:59 pm)`;
  // Default = current week, sent explicitly so the BE window always matches the coverage label.
  const filterWeek = getFilterWeek(resolvedYear, resolvedWeek);

  const headerFilters = (
    <Row gutter={[8, 8]} align="middle">
      <Col style={{ maxWidth: 149 }}>
        <PageStoreSelector stores={improveQscStoreCollection?.data} onChange={changeStore} allowAll />
      </Col>
      <Col>
        <PeriodFilterToolbar
          year={periodFilter.year}
          week={periodFilter.week}
          filterActive={periodFilter.open || !!storeCode}
          onWeekSelect={periodFilter.onWeekSelect}
          onOpen={periodFilter.openFilterCard}
        />
      </Col>
    </Row>
  );

  return (
    <Layout className="relative height-full flex flex__column">
      {isMobile ? (
        <PageHeaderMob
          title={
            <div className="mb-3 flex flex-align-center">
              {sidebarMenuIcons[ESidebarMenuKey.ImproveQsc]}
              <span className="ml-3 ff-regular">{ESidebarMenuLabel[ESidebarMenuKey.ImproveQsc]}</span>
            </div>
          }
          extra={[<span key="filters">{headerFilters}</span>]}
        />
      ) : (
        <PageHeader
          title={
            <div>
              <span>{ESidebarMenuLabel[ESidebarMenuKey.ImproveQsc]}</span>
            </div>
          }
          extra={headerFilters}
        />
      )}

      <PageContent>
        <div className="page-content__container">
          <div className="page-content__scroll-container scrollbar-y">
            <div style={{ overflow: 'hidden' }}>
              <Row gutter={16} className="mb-5">
                <Col span={14}>
                  <ImproveQscSummaryBlock
                    dateRange={coverage}
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
                <Col span={10}>
                  <ImproveQscFindingsBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
              </Row>

              <ImproveQscStoreCards
                params={{
                  storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                  week: filterWeek,
                }}
              />
            </div>
          </div>
        </div>

        <PeriodFilterCard
          open={periodFilter.open}
          year={periodFilter.year}
          week={periodFilter.week}
          onYearChange={periodFilter.changeYear}
          onWeekChange={periodFilter.changeWeek}
          onCancel={periodFilter.closeFilterCard}
        />
      </PageContent>
    </Layout>
  );
};
